import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { apiErr } from "../lib/utils";
import { Badge } from "./ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Droplets, Zap, CheckCircle2, CalendarRange, Building2 } from "lucide-react";
import { toast } from "sonner";

const slug = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");

function Cell({ present, open = true, testid }) {
  if (!open) {
    return (
      <div
        data-testid={testid}
        title="Not open this month"
        className="h-5 flex-1 min-w-[14px] rounded-[2px] bg-muted/20 border border-dashed border-border"
      />
    );
  }
  return (
    <div
      data-testid={testid}
      title={present ? "Bill present" : "Missing"}
      className={`h-5 flex-1 min-w-[14px] rounded-[2px] ${present ? "bg-[#00E65B]/80" : "bg-red-500/25 border border-red-500/40"}`}
    />
  );
}

function MonthRow({ icon: Icon, color, cells, missing, label }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 shrink-0 flex items-center gap-1 text-[10px] text-muted-foreground"><Icon className={`h-3 w-3 ${color}`} /> {label}</div>
      <div className="flex gap-0.5 flex-1">{cells}</div>
      <Badge variant={missing === 0 ? "success" : missing >= 6 ? "warning" : "outline"} className="text-[9px] w-14 justify-center shrink-0">{missing === 0 ? "complete" : `${missing} gap`}</Badge>
    </div>
  );
}

export function CompletenessTab() {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = (y) => {
    setLoading(true);
    api.get("/esg-records/completeness", { params: y ? { year: y } : {} })
      .then((r) => { setData(r.data); setYear(r.data.year); })
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(null); }, []);

  if (loading && !data) return <div className="p-6 text-muted-foreground font-mono text-sm" data-testid="completeness-loading">Loading completeness radar…</div>;
  if (!data) return null;
  const s = data.summary || {};

  const YearPicker = () => (
    (data.years || []).length > 1 && (
      <Select value={String(year)} onValueChange={(v) => { setYear(v); load(v); }}>
        <SelectTrigger className="w-28 h-9" data-testid="completeness-year-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          {data.years.map((y) => <SelectItem key={y} value={y} data-testid={`completeness-year-${y}`}>FY{y}</SelectItem>)}
        </SelectContent>
      </Select>
    )
  );

  return (
    <div className="space-y-4" data-testid="completeness-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div><div className="font-display font-semibold">Completeness Radar · FY{year}</div>
          <div className="text-xs text-muted-foreground">Every active site × every month of the reporting year — which water & electricity bills are in, and what's still missing.</div></div>
        <YearPicker />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="card-flat p-4" data-testid="completeness-coverage">
          <div className="flex items-center gap-2 text-muted-foreground mb-2"><CheckCircle2 className="h-4 w-4" /><span className="label-eyebrow text-[10px]">Coverage</span></div>
          <div className={`font-mono text-2xl font-bold ${s.coverage_pct >= 90 ? "text-[#00E65B]" : s.coverage_pct >= 50 ? "text-[#FFB800]" : "text-red-500"}`}>{s.coverage_pct ?? 0}<span className="text-xs text-muted-foreground font-sans ml-1">%</span></div>
          <div className="text-[11px] text-muted-foreground mt-0.5">{s.present ?? 0} of {s.expected_cells ?? 0} expected bills</div>
        </div>
        <div className="card-flat p-4" data-testid="completeness-missing">
          <div className="flex items-center gap-2 text-muted-foreground mb-2"><CalendarRange className="h-4 w-4" /><span className="label-eyebrow text-[10px]">Missing bills</span></div>
          <div className={`font-mono text-2xl font-bold ${(s.missing ?? 0) ? "text-red-500" : "text-[#00E65B]"}`}>{s.missing ?? 0}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">site-months with no bill</div>
        </div>
        <div className="card-flat p-4" data-testid="completeness-active">
          <div className="flex items-center gap-2 text-muted-foreground mb-2"><Building2 className="h-4 w-4" /><span className="label-eyebrow text-[10px]">Active sites</span></div>
          <div className="font-mono text-2xl font-bold">{s.active ?? 0}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">tracked this year</div>
        </div>
      </div>

      <div className="flex items-center gap-4 text-[10px] text-muted-foreground px-1">
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-[2px] bg-[#00E65B]/80 inline-block" /> bill present</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-[2px] bg-red-500/25 border border-red-500/40 inline-block" /> missing</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-[2px] bg-muted/20 border border-dashed border-border inline-block" /> not open</span>
        <span className="ml-auto flex gap-1">{(data.months || []).map((m) => <span key={m.m} className="w-[14px] text-center">{m.label[0]}</span>)}</span>
      </div>

      {(data.groups || []).length > 0 && (
        <div className="card-flat p-4" data-testid="completeness-by-group">
          <div className="label-eyebrow mb-2">Coverage by group — open months only</div>
          <div className="space-y-1.5">
            {data.groups.map((g) => (
              <div key={g.group} className="flex items-center justify-between gap-3 text-xs py-1 border-b border-border/40" data-testid={`completeness-group-row-${slug(g.group)}`}>
                <span className="truncate font-medium min-w-0">{g.group} <span className="text-muted-foreground font-normal">· {g.sites} site{g.sites > 1 ? "s" : ""}</span></span>
                <span className={`font-mono ${g.coverage_pct >= 90 ? "text-[#00E65B]" : g.coverage_pct >= 50 ? "text-[#FFB800]" : "text-red-500"}`}>{g.coverage_pct}% · {g.missing} missing</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(data.facilities || []).length === 0
        ? <div className="card-flat p-10 text-center text-muted-foreground" data-testid="completeness-empty">No active sites with utility data yet — add facilities in Settings → Facilities or assign bills to sites.</div>
        : <div className="space-y-2" data-testid="completeness-grid">
          {data.facilities.map((f) => (
            <div key={f.facility} className="card-flat p-3 space-y-1.5" data-testid={`completeness-row-${slug(f.facility)}`}>
              <div className="flex items-center gap-2 mb-1">
                <Building2 className="h-3.5 w-3.5 text-accent shrink-0" />
                <span className="font-medium text-sm truncate">{f.facility}</span>
                {f.group && <Badge variant="outline" className="text-[8px] shrink-0 text-accent/80" data-testid={`completeness-group-${slug(f.facility)}`}>{f.group}</Badge>}
                {f.close_date && <Badge variant="outline" className="text-[8px] shrink-0 text-red-400 border-red-400/40">Closed · {f.close_date}</Badge>}
                {!f.in_master && <Badge variant="outline" className="text-[8px] shrink-0" title="On records but not in the Settings master list">unlisted</Badge>}
              </div>
              {(() => {
                const expU = f.expected_utilities || ["water", "electricity"];
                if (expU.length === 0) {
                  return <div className="text-[10px] text-muted-foreground italic" data-testid={`completeness-awaiting-${slug(f.facility)}`}>Awaiting utility account setup — no billed accounts yet.</div>;
                }
                return (<>
                  {expU.includes("water") && (
                    <MonthRow icon={Droplets} color="text-[#38BDF8]" label="Water" missing={f.water_missing}
                      cells={f.water.map((c) => <Cell key={c.m} present={c.present} open={c.open !== false} testid={`completeness-cell-${slug(f.facility)}-water-${c.m}`} />)} />
                  )}
                  {expU.includes("electricity") && (
                    <MonthRow icon={Zap} color="text-[#FFB800]" label="Electricity" missing={f.electricity_missing}
                      cells={f.electricity.map((c) => <Cell key={c.m} present={c.present} open={c.open !== false} testid={`completeness-cell-${slug(f.facility)}-electricity-${c.m}`} />)} />
                  )}
                </>);
              })()}
            </div>
          ))}
        </div>}
    </div>
  );
}
